import { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  Form,
  FormAlerts,
  FormField,
  FormInputNumber,
  FormSelect,
} from '../../../common/form';
import { useFormContext, useWatch } from 'react-hook-form';
import { Padding } from '../../../common/components/Padding';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { confirmationHeaders } from '../../../common/utils';
import { useMutationErrors } from '../../../common/queryClient';
import { ActionButton } from '../../../common/components';
import translations from './UserFollowedDataObjectGroupModal.translations.json';
import { UserFollowedDataObjectGroupSchema } from './schema/UserFollowedDataObjectGroupSchema';
import { UserFollowedDataObjectGroupValidFormDataType } from './schema/types';
import { keepPreviousData } from '@tanstack/react-query';
import { useDataObjectGroupsWithDescriptionsQuery } from '../../../dataObjectGroup/query/useDataObjectGroupsWithDescriptionsQuery';
import { DataObjectGroupType } from '../../../dataobject/types';
import { useMutateUserFollowedDataObjectGroupQuery } from './query/useMutateUserFollowedDataObjectGroupQuery';
import { useGroupDescriptionOptions } from '../../../sample';

const { addTranslations, t } = i18nNsFactory(
  'user-followed-data-object-group-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface UserFollowedDataObjectGroupModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export interface GroupDescriptionOption {
  key: string;
  label: string;
  value: string | number;
  groupType: DataObjectGroupType;
}

export const UserFollowedDataObjectGroupModal: FC<
  UserFollowedDataObjectGroupModalProps
> = (props) => {
  const { onClose, onSuccess } = props;

  const { mutateAsync } = useMutateUserFollowedDataObjectGroupQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSubmit = useCallback(
    (
      values: UserFollowedDataObjectGroupValidFormDataType,
      confirm: boolean,
    ) => {
      clearMutationErrors();
      return mutateAsync({
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(onSuccess)
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [clearMutationErrors, mutateAsync, onSuccess, setMutationErrors],
  );
  return (
    <Form
      schema={UserFollowedDataObjectGroupSchema.validFormDataSchema}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="userFollowedDataObjectDroup"
    >
      <UserFollowedDataObjectGroupModalInner onClose={onClose} />
    </Form>
  );
};

function UserFollowedDataObjectGroupModalInner({
  onClose,
}: {
  onClose: () => void;
}) {
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const { setValue } = useFormContext();
  const groupType = useWatch({ name: 'groupType' });

  const { data: dataObjectGroupsWithDescriptions, isFetching } =
    useDataObjectGroupsWithDescriptionsQuery(
      { active: false },
      {
        placeholderData: keepPreviousData,
      },
    );
  const [descriptionGroupOptions, ungroupedDescriptionOptions] =
    useGroupDescriptionOptions(
      dataObjectGroupsWithDescriptions,
      true,
      'groupId',
    );

  const groupOptions = useMemo(
    () => [
      {
        key: 'description-groups',
        label: t('option-group.description-groups'),
        options: descriptionGroupOptions,
      },
      {
        key: 'ungrouped-descriptions',
        label: t('option-group.ungrouped-descriptions'),
        options: ungroupedDescriptionOptions,
      },
    ],
    [descriptionGroupOptions, ungroupedDescriptionOptions],
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      minWidth="30rem"
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <Flex>
            <FormField
              name="dataObjectGroupId"
              label={t('dataObjectGroupId.label')}
              size="small"
              required
            >
              <FormSelect
                name="dataObjectGroupId"
                placeholder={t('dataObjectGroupId.placeholder')}
                style={{ width: '25rem' }}
                styles={{
                  popup: { root: { width: 'max-content', maxHeight: 400 } },
                }}
                showSearch
                allowClear
                disabled={isFetching}
                loading={isFetching}
                options={groupOptions}
                filterOption={(input, option) =>
                  Boolean(
                    option?.label?.toLowerCase().includes(input.toLowerCase()),
                  )
                }
                onChange={(value, option) => {
                  setValue('groupType', option.groupType);
                }}
              />
            </FormField>
          </Flex>
          <Flex vertical gap="middle">
            <FormField
              name="periodSkipCount"
              label={t('periodSkipCount.label')}
              required={groupType === DataObjectGroupType.I}
            >
              <FormInputNumber
                style={{ maxWidth: '5rem' }}
                name="periodSkipCount"
                min="0"
                max="999"
                decimalSeparator=","
                disabled={groupType === DataObjectGroupType.O}
              />
            </FormField>
          </Flex>
        </Flex>
      </Padding>
    </Modal>
  );
}
